import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, ClickAwayListener, IconButton, Paper, Popper, Stack, TextField, useTheme } from "@mui/material";
import { useCallback, useState } from "react";
import type { FilterWidgetSettings } from "../../../../../api/types/dashboardsTypes";
import type { ConditionDescriptor } from "../../../../../api/types/fieldConfigurationTypes";
import type { DashboardFilter } from "../../dashboard.types";
import FilterValuesGrid from "./FilterValuesGrid";
import { toFilterValue } from "./filterValuesQuery";
import SelectedValueChips from "./SelectedValueChips";

const POPPER_MIN_HEIGHT = 340;
const POPPER_GAP = 8;

const getBoundaryBottom = (element: HTMLElement): number => {
  let node = element.parentElement;
  while (node !== null) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") {
      return node.getBoundingClientRect().bottom;
    }
    node = node.parentElement;
  }
  return window.innerHeight;
};

interface Props {
  filter: DashboardFilter;
  settings: FilterWidgetSettings;
  allowShowOnlySelected?: boolean;
  parentCondition?: ConditionDescriptor;
  onSettingsChange: (settings: Partial<FilterWidgetSettings>) => void;
}

const FilterDropdown = ({ filter, settings, allowShowOnlySelected, parentCondition, onSettingsChange }: Props) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popperHeight, setPopperHeight] = useState(POPPER_MIN_HEIGHT);

  const handleSelectedValuesChange = useCallback(
    (defaultValue: (string | null)[]) => onSettingsChange({ defaultValue }),
    [onSettingsChange]
  );

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl !== null) {
      setAnchorEl(null);
      return;
    }
    const { bottom } = event.currentTarget.getBoundingClientRect();
    const available = getBoundaryBottom(event.currentTarget) - bottom - POPPER_GAP * 2;
    setPopperHeight(Math.max(POPPER_MIN_HEIGHT, available));
    setAnchorEl(event.currentTarget);
  };

  const handleClickAway = () => setAnchorEl(null);

  const handleClearClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onSettingsChange({ defaultValue: [] });
    },
    [onSettingsChange]
  );

  const handleRemoveValue = useCallback(
    (index: number) => {
      const defaultValue = settings.defaultValue.filter((_, i) => i !== index).map(toFilterValue);
      onSettingsChange({ defaultValue });
    },
    [onSettingsChange, settings.defaultValue]
  );

  const selectedValues = settings.defaultValue;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, width: "100%" }}>
      <TextField
        placeholder={selectedValues.length === 0 ? "Select values" : undefined}
        fullWidth
        slotProps={{
          input: {
            readOnly: true,
            startAdornment:
              selectedValues.length > 0 ? (
                <SelectedValueChips values={selectedValues} onRemove={handleRemoveValue} />
              ) : undefined,
            endAdornment: (
              <Stack direction="row" sx={{ alignItems: "center" }}>
                {selectedValues.length > 0 && (
                  <IconButton onClick={handleClearClick}>
                    <CloseRoundedIcon />
                  </IconButton>
                )}
                <Box
                  component={ArrowDropDownRoundedIcon}
                  sx={{
                    color: "action.active",
                    transition: "transform 0.2s",
                    ...(anchorEl !== null && { transform: "rotate(180deg)" }),
                  }}
                />
              </Stack>
            ),
          },
        }}
        sx={{
          cursor: "pointer",
          ".MuiOutlinedInput-root": { gap: 0.5, ...(selectedValues.length > 0 && { py: 0.5 }) },
          input: {
            cursor: "pointer",
            ...(selectedValues.length > 0 && { width: 0, minWidth: 0, p: 0 }),
          },
        }}
        onClick={handleTriggerClick}
      />
      <Popper
        open={anchorEl !== null}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: theme.zIndex.modal, width: anchorEl?.clientWidth }}
      >
        <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={handleClickAway}>
          <Paper elevation={8} sx={{ p: 1, display: "flex", height: popperHeight }}>
            <FilterValuesGrid
              filter={filter}
              selectedValues={selectedValues}
              singleSelect={settings.singleSelect}
              hideSecondaryColumn={settings.hideSecondaryColumn}
              allowShowOnlySelected={allowShowOnlySelected}
              parentCondition={parentCondition}
              onSelectedValuesChange={handleSelectedValuesChange}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default FilterDropdown;
